/** Users & roles page strings (user menu → Users & roles), merged into the app's translation namespace on import. */
import i18n from '@/app/i18n'

const en = {
    'team.sendLink': 'Send link',
    'team.form.sendLink': 'E-mail the user a link to set their own password',
    'team.form.mailLanguage': 'E-mail language',
    'team.link.title': 'Send a set-password link',
    'team.link.confirm': 'We will e-mail {{email}} a link {{name}} can use to set their own password. It works for 72 hours, and an earlier link stops working.',
    'team.link.send': 'Send',
    'team.link.sent': 'Link sent to {{email}}. It works for {{hours}} hours.',
    'team.added.linkSent': 'User added. We e-mailed {{email}} a link to set a password; it works for {{hours}} hours.',
    'team.added.mailFailed': 'The e-mail was not sent: {{reason}} Hand over this password yourself.',
    'team.error.mail_not_configured': 'Sending e-mail is not set up on this server.',
    'team.error.mail_failed': 'The e-mail could not be sent. Try again later.',
    'team.error.too_many_links': 'This user has had 5 links in the last hour. Try again later.',
    'team.roles.pagesCount': 'pages {{count}}/{{total}}',
    'team.roles.all': 'Select all',
    'team.roles.none': 'Clear all',
    'team.roles.unsaved': 'Unsaved changes',
    'team.role.startFrom': 'Permissions',
    'team.role.startEmpty': 'No pages yet',
    'team.role.copyOf': 'Same as {{role}}',
    'team.menu': 'Users & roles',
    'team.title': 'Users & roles',
    'team.description': 'Add users, give them a role and choose which pages each role sees. Admins are managed by DistribuAI.',
    'team.tabs': 'Users and roles',
    'team.tab.users': 'Users',
    'team.tab.roles': 'Roles',
    'team.addUser': 'Add user',
    'team.addRole': 'Add role',
    'team.col.name': 'Name',
    'team.col.email': 'E-mail',
    'team.col.role': 'Role',
    'team.col.lastLogin': 'Last sign-in',
    'team.you': 'You',
    'team.disabled': 'Disabled',
    'team.managedByDistribuai': 'managed by DistribuAI',
    'team.never': 'Never',
    'team.resetPassword': 'Reset password',
    'team.remove': 'Remove',
    'team.roleOf': 'Role of {{name}}',
    'team.form.name': 'Name',
    'team.form.email': 'E-mail',
    'team.form.role': 'Role',
    'team.form.submit': 'Add',
    'team.cancel': 'Cancel',
    'team.done': 'Done',
    'team.close': 'Close',
    'team.save': 'Save',
    'team.copy': 'Copy',
    'team.copied': 'Copied',
    'team.added.title': 'User added',
    'team.passphrase.intro': 'Give {{name}} this password. It is shown only now; you can reset it later.',
    'team.passphrase.signIn': 'Signs in with {{email}}',
    'team.reset.title': 'Reset password',
    'team.reset.confirm': '{{name}} gets a new password and is signed out everywhere. You hand over the new password.',
    'team.reset.submit': 'Reset',
    'team.reset.done': 'New password',
    'team.remove.title': 'Remove user',
    'team.remove.confirm': '{{name}} loses access and is signed out. You can add them again later.',
    'team.remove.submit': 'Remove',
    'team.noCustomRoles': 'Add a role first (Roles tab), then users.',
    'team.roles.intro': 'Choose a role, then tick the pages it sees. Admins see everything. New pages stay hidden from roles until you allow them here.',
    'team.roles.pages': 'Pages',
    'team.roles.actions': 'Actions',
    'team.roles.decide': 'Approve and dismiss recommendations',
    'team.roles.decideHint': 'Without it the role sees recommendations but cannot decide.',
    'team.roles.users': 'Users: {{count}}',
    'team.roles.save': 'Save changes',
    'team.roles.discard': 'Discard changes',
    'team.roles.saved': 'Saved.',
    'team.roles.rename': 'Rename',
    'team.roles.delete': 'Delete',
    'team.roles.empty': 'No roles yet. Add the first one, then tick the pages it may see.',
    'team.role.name': 'Role name',
    'team.role.hint': 'Capital letters, digits and _, e.g. BUYER or STORE_MANAGER.',
    'team.role.addTitle': 'Add role',
    'team.role.renameTitle': 'Rename role',
    'team.role.deleteTitle': 'Delete role',
    'team.role.deleteConfirm': 'Delete the role {{role}}?',
    'team.loading': 'Loading…',
    'team.loadFailed': 'Users and roles could not be loaded.',
    'team.retry': 'Try again',
    'team.sortAsc': 'Ascending',
    'team.sortDesc': 'Descending',
    'team.columnMenu': '{{header}} column menu',
    'team.empty': 'No users yet.',
    'team.error.generic': 'The change could not be saved.',
    'team.error.exists_here': 'This person is already a user here.',
    'team.error.exists_elsewhere': 'This e-mail has an account elsewhere. Ask DistribuAI to add it.',
    'team.error.invalid_email': 'Check the e-mail address.',
    'team.error.invalid_name': 'A name is required.',
    'team.error.invalid_role_name': 'Use 2–50 capital letters, digits or _.',
    'team.error.reserved_role_name': 'That name belongs to an admin role.',
    'team.error.duplicate': 'A role with that name already exists.',
    'team.error.role_in_use': 'Give its users another role first.',
}

const et: typeof en = {
    'team.sendLink': 'Saada link',
    'team.form.sendLink': 'Saada kasutajale e-postiga link, et ta saaks ise parooli määrata',
    'team.form.mailLanguage': 'E-kirja keel',
    'team.link.title': 'Saada parooli määramise link',
    'team.link.confirm': 'Saadame aadressile {{email}} lingi, millega {{name}} saab endale parooli määrata. Link kehtib 72 tundi ja varem saadetud link lakkab töötamast.',
    'team.link.send': 'Saada',
    'team.link.sent': 'Link saadetud aadressile {{email}}. See kehtib {{hours}} tundi.',
    'team.added.linkSent': 'Kasutaja lisatud. Saatsime aadressile {{email}} lingi parooli määramiseks; see kehtib {{hours}} tundi.',
    'team.added.mailFailed': 'E-kiri jäi saatmata: {{reason}} Anna see parool kasutajale ise edasi.',
    'team.error.mail_not_configured': 'E-posti saatmine pole selles keskkonnas seadistatud.',
    'team.error.mail_failed': 'E-kirja ei õnnestunud saata. Proovi hiljem uuesti.',
    'team.error.too_many_links': 'Sellele kasutajale on viimase tunni jooksul saadetud juba 5 linki. Proovi hiljem uuesti.',
    'team.roles.pagesCount': 'lehti {{count}}/{{total}}',
    'team.roles.all': 'Vali kõik',
    'team.roles.none': 'Eemalda kõik',
    'team.roles.unsaved': 'Salvestamata muudatused',
    'team.role.startFrom': 'Õigused',
    'team.role.startEmpty': 'Lehtedeta',
    'team.role.copyOf': 'Nagu rollil {{role}}',
    'team.menu': 'Kasutajad ja rollid',
    'team.title': 'Kasutajad ja rollid',
    'team.description': 'Lisa kasutajaid, anna neile roll ja vali, milliseid lehti iga roll näeb. Administraatoreid haldab DistribuAI.',
    'team.tabs': 'Kasutajad ja rollid',
    'team.tab.users': 'Kasutajad',
    'team.tab.roles': 'Rollid',
    'team.addUser': 'Lisa kasutaja',
    'team.addRole': 'Lisa roll',
    'team.col.name': 'Nimi',
    'team.col.email': 'E-post',
    'team.col.role': 'Roll',
    'team.col.lastLogin': 'Viimati sisse loginud',
    'team.you': 'Sina',
    'team.disabled': 'Keelatud',
    'team.managedByDistribuai': 'haldab DistribuAI',
    'team.never': 'Mitte kunagi',
    'team.resetPassword': 'Lähtesta parool',
    'team.remove': 'Eemalda',
    'team.roleOf': 'Kasutaja {{name}} roll',
    'team.form.name': 'Nimi',
    'team.form.email': 'E-post',
    'team.form.role': 'Roll',
    'team.form.submit': 'Lisa',
    'team.cancel': 'Tühista',
    'team.done': 'Valmis',
    'team.close': 'Sulge',
    'team.save': 'Salvesta',
    'team.copy': 'Kopeeri',
    'team.copied': 'Kopeeritud',
    'team.added.title': 'Kasutaja lisatud',
    'team.passphrase.intro': 'Anna kasutajale {{name}} see parool. Seda näidatakse ainult praegu; vajadusel saad selle hiljem lähtestada.',
    'team.passphrase.signIn': 'Sisselogimise e-post: {{email}}',
    'team.reset.title': 'Lähtesta parool',
    'team.reset.confirm': 'Kasutaja {{name}} saab uue parooli ja logitakse kõikjalt välja. Uue parooli annad talle sina.',
    'team.reset.submit': 'Lähtesta',
    'team.reset.done': 'Uus parool',
    'team.remove.title': 'Eemalda kasutaja',
    'team.remove.confirm': 'Kasutaja {{name}} kaotab ligipääsu ja logitakse välja. Saad ta hiljem uuesti lisada.',
    'team.remove.submit': 'Eemalda',
    'team.noCustomRoles': 'Lisa enne roll (vahekaart Rollid), siis kasutajad.',
    'team.roles.intro': 'Vali roll ja märgi lehed, mida see näeb. Administraatorid näevad kõike. Uued lehed on rollidele peidetud, kuni need siin lubad.',
    'team.roles.pages': 'Lehed',
    'team.roles.actions': 'Tegevused',
    'team.roles.decide': 'Soovituste kinnitamine ja tagasilükkamine',
    'team.roles.decideHint': 'Ilma selleta näeb roll soovitusi, kuid ei saa otsustada.',
    'team.roles.users': 'Kasutajaid: {{count}}',
    'team.roles.save': 'Salvesta muudatused',
    'team.roles.discard': 'Tühista muudatused',
    'team.roles.saved': 'Salvestatud.',
    'team.roles.rename': 'Nimeta ümber',
    'team.roles.delete': 'Kustuta',
    'team.roles.empty': 'Rolle veel pole. Lisa esimene ja märgi lehed, mida see näeb.',
    'team.role.name': 'Rolli nimi',
    'team.role.hint': 'Suurtähed, numbrid ja _, nt OSTJA või POE_JUHT.',
    'team.role.addTitle': 'Lisa roll',
    'team.role.renameTitle': 'Nimeta roll ümber',
    'team.role.deleteTitle': 'Kustuta roll',
    'team.role.deleteConfirm': 'Kustutada roll {{role}}?',
    'team.loading': 'Laadin…',
    'team.loadFailed': 'Kasutajaid ja rolle ei õnnestunud laadida.',
    'team.retry': 'Proovi uuesti',
    'team.sortAsc': 'Kasvav',
    'team.sortDesc': 'Kahanev',
    'team.columnMenu': 'Veeru {{header}} menüü',
    'team.empty': 'Kasutajaid veel pole.',
    'team.error.generic': 'Muudatust ei õnnestunud salvestada.',
    'team.error.exists_here': 'See inimene on siin juba kasutaja.',
    'team.error.exists_elsewhere': 'Selle e-postiga konto on mujal kasutusel. Palu DistribuAI-l see lisada.',
    'team.error.invalid_email': 'Kontrolli e-posti aadressi.',
    'team.error.invalid_name': 'Nimi on kohustuslik.',
    'team.error.invalid_role_name': 'Kasuta 2–50 suurtähte, numbrit või _.',
    'team.error.reserved_role_name': 'See nimi kuulub administraatori rollile.',
    'team.error.duplicate': 'Sellise nimega roll on juba olemas.',
    'team.error.role_in_use': 'Anna enne selle kasutajatele teine roll.',
}

const de: typeof en = {
    'team.sendLink': 'Link senden',
    'team.form.sendLink': 'Dem Benutzer per E-Mail einen Link senden, um selbst ein Passwort festzulegen',
    'team.form.mailLanguage': 'Sprache der E-Mail',
    'team.link.title': 'Link zum Festlegen des Passworts senden',
    'team.link.confirm': 'Wir senden an {{email}} einen Link, mit dem {{name}} selbst ein Passwort festlegen kann. Er gilt 72 Stunden, ein früherer Link wird ungültig.',
    'team.link.send': 'Senden',
    'team.link.sent': 'Link an {{email}} gesendet. Er gilt {{hours}} Stunden.',
    'team.added.linkSent': 'Benutzer hinzugefügt. Wir haben an {{email}} einen Link zum Festlegen des Passworts gesendet; er gilt {{hours}} Stunden.',
    'team.added.mailFailed': 'Die E-Mail wurde nicht gesendet: {{reason}} Geben Sie dieses Passwort selbst weiter.',
    'team.error.mail_not_configured': 'Der E-Mail-Versand ist auf diesem Server nicht eingerichtet.',
    'team.error.mail_failed': 'Die E-Mail konnte nicht gesendet werden. Bitte später erneut versuchen.',
    'team.error.too_many_links': 'Dieser Benutzer hat in der letzten Stunde bereits 5 Links erhalten. Bitte später erneut versuchen.',
    'team.roles.pagesCount': 'Seiten {{count}}/{{total}}',
    'team.roles.all': 'Alle wählen',
    'team.roles.none': 'Alle abwählen',
    'team.roles.unsaved': 'Ungespeicherte Änderungen',
    'team.role.startFrom': 'Rechte',
    'team.role.startEmpty': 'Noch keine Seiten',
    'team.role.copyOf': 'Wie {{role}}',
    'team.menu': 'Benutzer & Rollen',
    'team.title': 'Benutzer & Rollen',
    'team.description': 'Fügen Sie Benutzer hinzu, geben Sie ihnen eine Rolle und wählen Sie, welche Seiten jede Rolle sieht. Administratoren verwaltet DistribuAI.',
    'team.tabs': 'Benutzer und Rollen',
    'team.tab.users': 'Benutzer',
    'team.tab.roles': 'Rollen',
    'team.addUser': 'Benutzer hinzufügen',
    'team.addRole': 'Rolle hinzufügen',
    'team.col.name': 'Name',
    'team.col.email': 'E-Mail',
    'team.col.role': 'Rolle',
    'team.col.lastLogin': 'Letzte Anmeldung',
    'team.you': 'Sie',
    'team.disabled': 'Deaktiviert',
    'team.managedByDistribuai': 'verwaltet DistribuAI',
    'team.never': 'Nie',
    'team.resetPassword': 'Passwort zurücksetzen',
    'team.remove': 'Entfernen',
    'team.roleOf': 'Rolle von {{name}}',
    'team.form.name': 'Name',
    'team.form.email': 'E-Mail',
    'team.form.role': 'Rolle',
    'team.form.submit': 'Hinzufügen',
    'team.cancel': 'Abbrechen',
    'team.done': 'Fertig',
    'team.close': 'Schließen',
    'team.save': 'Speichern',
    'team.copy': 'Kopieren',
    'team.copied': 'Kopiert',
    'team.added.title': 'Benutzer hinzugefügt',
    'team.passphrase.intro': 'Geben Sie {{name}} dieses Passwort. Es wird nur jetzt angezeigt; Sie können es später zurücksetzen.',
    'team.passphrase.signIn': 'Anmeldung mit {{email}}',
    'team.reset.title': 'Passwort zurücksetzen',
    'team.reset.confirm': '{{name}} erhält ein neues Passwort und wird überall abgemeldet. Das neue Passwort geben Sie weiter.',
    'team.reset.submit': 'Zurücksetzen',
    'team.reset.done': 'Neues Passwort',
    'team.remove.title': 'Benutzer entfernen',
    'team.remove.confirm': '{{name}} verliert den Zugang und wird abgemeldet. Sie können die Person später wieder hinzufügen.',
    'team.remove.submit': 'Entfernen',
    'team.noCustomRoles': 'Legen Sie zuerst eine Rolle an (Reiter Rollen), dann Benutzer.',
    'team.roles.intro': 'Wählen Sie eine Rolle und haken Sie die Seiten an, die sie sieht. Administratoren sehen alles. Neue Seiten bleiben Rollen verborgen, bis Sie sie hier erlauben.',
    'team.roles.pages': 'Seiten',
    'team.roles.actions': 'Aktionen',
    'team.roles.decide': 'Empfehlungen annehmen und ablehnen',
    'team.roles.decideHint': 'Ohne dies sieht die Rolle Empfehlungen, kann aber nicht entscheiden.',
    'team.roles.users': 'Benutzer: {{count}}',
    'team.roles.save': 'Änderungen speichern',
    'team.roles.discard': 'Änderungen verwerfen',
    'team.roles.saved': 'Gespeichert.',
    'team.roles.rename': 'Umbenennen',
    'team.roles.delete': 'Löschen',
    'team.roles.empty': 'Noch keine Rollen. Legen Sie die erste an und haken Sie die Seiten an, die sie sieht.',
    'team.role.name': 'Rollenname',
    'team.role.hint': 'Großbuchstaben, Ziffern und _, z. B. EINKAUF oder FILIALLEITUNG.',
    'team.role.addTitle': 'Rolle hinzufügen',
    'team.role.renameTitle': 'Rolle umbenennen',
    'team.role.deleteTitle': 'Rolle löschen',
    'team.role.deleteConfirm': 'Die Rolle {{role}} löschen?',
    'team.loading': 'Wird geladen…',
    'team.loadFailed': 'Benutzer und Rollen konnten nicht geladen werden.',
    'team.retry': 'Erneut versuchen',
    'team.sortAsc': 'Aufsteigend',
    'team.sortDesc': 'Absteigend',
    'team.columnMenu': 'Spaltenmenü {{header}}',
    'team.empty': 'Noch keine Benutzer.',
    'team.error.generic': 'Die Änderung konnte nicht gespeichert werden.',
    'team.error.exists_here': 'Diese Person ist hier bereits Benutzer.',
    'team.error.exists_elsewhere': 'Diese E-Mail hat anderswo ein Konto. Bitten Sie DistribuAI, es hinzuzufügen.',
    'team.error.invalid_email': 'Bitte die E-Mail-Adresse prüfen.',
    'team.error.invalid_name': 'Ein Name ist erforderlich.',
    'team.error.invalid_role_name': '2–50 Großbuchstaben, Ziffern oder _ verwenden.',
    'team.error.reserved_role_name': 'Dieser Name gehört zu einer Administratorrolle.',
    'team.error.duplicate': 'Eine Rolle mit diesem Namen gibt es bereits.',
    'team.error.role_in_use': 'Geben Sie ihren Benutzern zuerst eine andere Rolle.',
}

const ru: typeof en = {
    'team.sendLink': 'Отправить ссылку',
    'team.form.sendLink': 'Отправить пользователю ссылку, чтобы он сам задал пароль',
    'team.form.mailLanguage': 'Язык письма',
    'team.link.title': 'Отправить ссылку для пароля',
    'team.link.confirm': 'Мы отправим на {{email}} ссылку, по которой {{name}} сможет сам задать пароль. Она действует 72 часа, прежняя ссылка перестанет работать.',
    'team.link.send': 'Отправить',
    'team.link.sent': 'Ссылка отправлена на {{email}}. Она действует {{hours}} ч.',
    'team.added.linkSent': 'Пользователь добавлен. Мы отправили на {{email}} ссылку для задания пароля; она действует {{hours}} ч.',
    'team.added.mailFailed': 'Письмо не отправлено: {{reason}} Передайте этот пароль сами.',
    'team.error.mail_not_configured': 'Отправка почты на этом сервере не настроена.',
    'team.error.mail_failed': 'Не удалось отправить письмо. Попробуйте позже.',
    'team.error.too_many_links': 'Этому пользователю за последний час уже отправлено 5 ссылок. Попробуйте позже.',
    'team.roles.pagesCount': 'страниц {{count}}/{{total}}',
    'team.roles.all': 'Выбрать все',
    'team.roles.none': 'Снять все',
    'team.roles.unsaved': 'Несохранённые изменения',
    'team.role.startFrom': 'Права',
    'team.role.startEmpty': 'Пока без страниц',
    'team.role.copyOf': 'Как у роли {{role}}',
    'team.menu': 'Пользователи и роли',
    'team.title': 'Пользователи и роли',
    'team.description': 'Добавляйте пользователей, назначайте им роль и выбирайте, какие страницы видит каждая роль. Администраторами управляет DistribuAI.',
    'team.tabs': 'Пользователи и роли',
    'team.tab.users': 'Пользователи',
    'team.tab.roles': 'Роли',
    'team.addUser': 'Добавить пользователя',
    'team.addRole': 'Добавить роль',
    'team.col.name': 'Имя',
    'team.col.email': 'Эл. почта',
    'team.col.role': 'Роль',
    'team.col.lastLogin': 'Последний вход',
    'team.you': 'Вы',
    'team.disabled': 'Отключён',
    'team.managedByDistribuai': 'управляет DistribuAI',
    'team.never': 'Никогда',
    'team.resetPassword': 'Сбросить пароль',
    'team.remove': 'Удалить',
    'team.roleOf': 'Роль пользователя {{name}}',
    'team.form.name': 'Имя',
    'team.form.email': 'Эл. почта',
    'team.form.role': 'Роль',
    'team.form.submit': 'Добавить',
    'team.cancel': 'Отмена',
    'team.done': 'Готово',
    'team.close': 'Закрыть',
    'team.save': 'Сохранить',
    'team.copy': 'Копировать',
    'team.copied': 'Скопировано',
    'team.added.title': 'Пользователь добавлен',
    'team.passphrase.intro': 'Передайте {{name}} этот пароль. Он показывается только сейчас; позже его можно сбросить.',
    'team.passphrase.signIn': 'Вход по адресу {{email}}',
    'team.reset.title': 'Сбросить пароль',
    'team.reset.confirm': '{{name}} получит новый пароль и выйдет из всех сеансов. Новый пароль передадите вы.',
    'team.reset.submit': 'Сбросить',
    'team.reset.done': 'Новый пароль',
    'team.remove.title': 'Удалить пользователя',
    'team.remove.confirm': '{{name}} потеряет доступ и выйдет из системы. Позже его можно добавить снова.',
    'team.remove.submit': 'Удалить',
    'team.noCustomRoles': 'Сначала добавьте роль (вкладка «Роли»), затем пользователей.',
    'team.roles.intro': 'Выберите роль и отметьте страницы, которые она видит. Администраторы видят всё. Новые страницы скрыты от ролей, пока вы их здесь не разрешите.',
    'team.roles.pages': 'Страницы',
    'team.roles.actions': 'Действия',
    'team.roles.decide': 'Принимать и отклонять рекомендации',
    'team.roles.decideHint': 'Без этого роль видит рекомендации, но не может принимать решения.',
    'team.roles.users': 'Пользователей: {{count}}',
    'team.roles.save': 'Сохранить изменения',
    'team.roles.discard': 'Отменить изменения',
    'team.roles.saved': 'Сохранено.',
    'team.roles.rename': 'Переименовать',
    'team.roles.delete': 'Удалить',
    'team.roles.empty': 'Ролей пока нет. Добавьте первую и отметьте страницы, которые она видит.',
    'team.role.name': 'Название роли',
    'team.role.hint': 'Заглавные латинские буквы, цифры и _, напр. BUYER или STORE_MANAGER.',
    'team.role.addTitle': 'Добавить роль',
    'team.role.renameTitle': 'Переименовать роль',
    'team.role.deleteTitle': 'Удалить роль',
    'team.role.deleteConfirm': 'Удалить роль {{role}}?',
    'team.loading': 'Загрузка…',
    'team.loadFailed': 'Не удалось загрузить пользователей и роли.',
    'team.retry': 'Повторить',
    'team.sortAsc': 'По возрастанию',
    'team.sortDesc': 'По убыванию',
    'team.columnMenu': 'Меню столбца {{header}}',
    'team.empty': 'Пользователей пока нет.',
    'team.error.generic': 'Не удалось сохранить изменение.',
    'team.error.exists_here': 'Этот человек уже пользователь здесь.',
    'team.error.exists_elsewhere': 'Этот адрес уже используется в другой учётной записи. Попросите DistribuAI добавить его.',
    'team.error.invalid_email': 'Проверьте адрес эл. почты.',
    'team.error.invalid_name': 'Имя обязательно.',
    'team.error.invalid_role_name': 'Используйте 2–50 заглавных букв, цифр или _.',
    'team.error.reserved_role_name': 'Это имя принадлежит роли администратора.',
    'team.error.duplicate': 'Роль с таким названием уже есть.',
    'team.error.role_in_use': 'Сначала назначьте её пользователям другую роль.',
}

for (const [language, strings] of Object.entries({ en, et, de, ru })) {
    i18n.addResourceBundle(language, 'translation', strings, true, false)
}

/** "no pages" for a role that may see none (the app's index route). */
const access = {
    en: { 'access.noPages': 'Your role has no pages yet. Ask your administrator for access.' },
    et: { 'access.noPages': 'Sinu rollil pole veel ühtegi lehte. Küsi ligipääsu oma administraatorilt.' },
    de: { 'access.noPages': 'Ihre Rolle hat noch keine Seiten. Bitten Sie Ihren Administrator um Zugriff.' },
    ru: { 'access.noPages': 'У вашей роли пока нет страниц. Попросите доступ у администратора.' },
}
for (const [language, strings] of Object.entries(access)) {
    i18n.addResourceBundle(language, 'translation', strings, true, false)
}
